import { TRACK_ID, Track } from '@hugendubel/tracking_manager';
import { _ } from '@hugendubel/utils';
import { Type, ClientError, ClientErrorCode, Money, WsAvailabilityData, createCall, Method, TokenChecks, WsParser } from '@hugendubel/ws_client';
import { Persistore } from 'persistore';
import { Prudsys } from '@hugendubel/prudsys';

const Globals = { window: () => window, document: () => document };

const parse$8 = ({ greetingCardCode, giftWrapPaperCode, greetingText, active }) => ({
    greetingCardCode: Type.id(greetingCardCode, 'GiftWrap.greetingCardCode'),
    giftWrapPaperCode: Type.id(giftWrapPaperCode, 'GiftWrap.giftWrapPaperCode'),
    greetingText: Type.optional.string(greetingText, 'GiftWrap.greetingText'),
    active: Type.boolean(active, 'GiftWrap.active'),
});
const equals$5 = (lhs, rhs) => {
    if (lhs === undefined)
        return rhs === undefined;
    else if (rhs === undefined)
        return false;
    return (lhs.greetingCardCode === rhs.greetingCardCode &&
        lhs.giftWrapPaperCode === rhs.giftWrapPaperCode &&
        lhs.greetingText === rhs.greetingText &&
        lhs.active === rhs.active);
};
const forTracking$2 = ({ active }) => ({ active });
const WsGiftWrap = { parse: parse$8, equals: equals$5, forTracking: forTracking$2 };

var CartItemType;
(function (CartItemType) {
    CartItemType["DEFAULT"] = "DEFAULT";
    CartItemType["COUPON"] = "COUPON";
    CartItemType["SERIAL_BUNDLE"] = "SERIAL";
    CartItemType["DOWNLOAD_GIFT"] = "DL_GIFT";
})(CartItemType || (CartItemType = {}));
const parseItemType = (raw) => {
    if (!raw) {
        return CartItemType.DEFAULT;
    }
    const rawString = Type.string(raw, 'ItemType');
    const type = Object.values(CartItemType).find(v => v === rawString);
    if (!type) {
        throw new ClientError(TRACK_ID('2019-01-02 02:25:07'), ClientErrorCode.UNEXPECTED_TYPE, `CartItem type '${rawString}' unknown.`);
    }
    return type;
};
const serializeItemType = (type) => {
    if (type !== CartItemType.DEFAULT) {
        return type;
    }
    return undefined;
};
const parseCouponData = (json) => ({
    value: Money.parse(Type.number(json.value, 'couponData.value')),
    from: Type.string(json.from, 'couponData.from'),
    to: Type.string(json.to, 'couponData.to'),
    message: Type.optional.string(json.message, 'couponData.message'),
});
const parseCouponDataIfRequired = (couponDataJson, itemType) => {
    // coupon data is only required for coupon items
    if (itemType !== CartItemType.COUPON) {
        return undefined;
    }
    return parseCouponData(Type.object(couponDataJson, 'CouponData'));
};

const _parseCommon = (json) => ({
    senderName: Type.string(json.senderName, 'giftInfo.senderName'),
    messageSalutation: Type.string(json.messageSalutation, 'giftInfo.messageSalutation'),
    message: Type.optional.string(json.message, 'giftInfo.message'),
    receiverEmail: Type.string(json.receiverEmail, 'giftInfo.receiverEmail'),
});
const parse$7 = (json) => ({
    ..._parseCommon(json),
    deliveryDate: Type.dateString(json.deliveryDate, 'giftInfo.deliveryDate'),
});
const parseSerialized$6 = (json) => ({
    ..._parseCommon(json),
    deliveryDate: Type.string(json.deliveryDate, 'giftInfo.deliveryDate'),
});
const serialize$6 = ({ deliveryDate, senderName, messageSalutation, message, receiverEmail, }) => ({
    deliveryDate: deliveryDate.toISOString(),
    senderName,
    messageSalutation,
    message,
    receiverEmail,
});
const WsGiftInfo = { parse: parse$7, serialize: serialize$6, parseSerialized: parseSerialized$6 };

const _commonParse$2 = (json, type) => ({
    state: Type.string(json.state, 'InvalidShoppingCartItem.state'),
    articleId: Type.optional.number(json.articleId, 'InvalidShoppingCartItem.articleId'),
    amount: Type.number(json.amount, 'InvalidShoppingCartItem.amount'),
    itemRef: Type.string(json.itemRef, 'InvalidShoppingCartItem.itemRef'),
    bundleId: Type.optional.number(json.bundleId, 'InvalidShoppingCartItem.bundleId'),
    couponData: parseCouponDataIfRequired(json.couponData, type),
});
const parse$6 = (json) => {
    const type = parseItemType(json.type);
    return {
        ..._commonParse$2(json, type),
        title: Type.optional.string(json.title, 'InvalidShoppingCartItem.title'),
        type,
        giftInfo: type === CartItemType.DOWNLOAD_GIFT ? WsGiftInfo.parse(json.giftInfo) : undefined,
    };
};
const parseSerialized$5 = (json) => {
    const type = serializeItemType(json.type);
    return {
        ..._commonParse$2(json, type),
        type,
        giftInfo: type === CartItemType.DOWNLOAD_GIFT ? WsGiftInfo.parseSerialized(json.giftInfo) : undefined,
    };
};
const serialize$5 = ({ amount, itemRef, state, articleId, type, couponData, bundleId, giftInfo, }) => {
    const base = {
        amount,
        itemRef,
        state,
        articleId,
        type: serializeItemType(type),
    };
    if (type === CartItemType.COUPON) {
        return {
            ...base,
            couponData: couponData,
        };
    }
    else if (type === CartItemType.DOWNLOAD_GIFT) {
        return {
            ...base,
            giftInfo: giftInfo && WsGiftInfo.serialize(giftInfo),
        };
    }
    else if (type === CartItemType.SERIAL_BUNDLE) {
        return {
            ...base,
            bundleId,
        };
    }
    return base;
};
const forTracking$1 = (items) => items.map(({ articleId }) => ({ articleId }));
const equals$4 = (lhs, rhs) => lhs.state === rhs.state &&
    lhs.articleId === rhs.articleId &&
    lhs.amount === rhs.amount &&
    lhs.itemRef === rhs.itemRef &&
    serializeItemType(lhs.type) === rhs.type;
const WsInvalidShoppingCartItem = { parse: parse$6, equals: equals$4, serialize: serialize$5, parseSerialized: parseSerialized$5, forTracking: forTracking$1 };

const parseShoppingCart = (json) => ({
    showDownloadLabel: Type.boolean(json.showDownloadLabel, 'ShoppingCartItemFeatures.showDownloadLabel'),
    canBookmark: Type.boolean(json.canBookmark, 'ShoppingCartItemFeatures.canBookmark'),
    canChangeAmount: Type.boolean(json.canChangeAmount, 'ShoppingCartItemFeatures.canChangeAmount'),
    showDownloadLink: false,
    showReadingLink: false,
});
const parseOrder = (json) => ({
    showDownloadLabel: Type.boolean(json.showDownloadLabel, 'ShoppingCartItemFeatures.showDownloadLabel'),
    canBookmark: false,
    canChangeAmount: false,
    showDownloadLink: Type.boolean(json.showDownloadLink, 'ShoppingCartItemFeatures.showDownloadLink'),
    showReadingLink: Type.boolean(json.showReadingLink, 'ShoppingCartItemFeatures.showReadingLink'),
});
const WsItemFeatures = { parseShoppingCart, parseOrder };

const parseSelectedArticle = (json) => ({
    categoryBundleProductId: Type.number(json.categoryBundleProductId, 'SelectedBundleArticle.categoryBundleProductId'),
    title: Type.falsyToUndefined.string(json.title, 'SelectedBundleArticle.title'),
});
const parseSerializedSelectedArticle = (json) => ({
    categoryBundleProductId: Type.number(json.categoryBundleProductId, 'SerializedSelectedBundleArticle.categoryBundleProductId'),
});
const serializeSelectedArticle = ({ categoryBundleProductId, }) => ({ categoryBundleProductId });
const serializeBundleSelection = ({ category, selectedItems }) => ({
    category,
    selectedItems: selectedItems.map(serializeSelectedArticle),
});
const parseBundleSelection = (json) => ({
    category: Type.number(json.category, 'BundleSelection.category'),
    selectedItems: Type.array(json.selectedItems, 'BundleSelection.selectedItems').map(parseSelectedArticle),
});
const parseSerializedBundleSelection = (json) => ({
    category: Type.number(json.category, 'SerializedBundleSelection.category'),
    selectedItems: Type.array(json.selectedItems, 'SerializedBundleSelection.selectedItems').map(parseSerializedSelectedArticle),
});
const WsBundleSelection = {
    parse: parseBundleSelection,
    parseSerialized: parseSerializedBundleSelection,
    serialize: serializeBundleSelection,
};

const ItemSaleRestrictions = Object.freeze({
    NO_RESTRICTION: 0,
    DOWNLOAD_NOT_AVAILABLE_IN_COUNTRY: 1,
    NO_SHIPMENT_TO_COUNTRY: 2,
    DUPLICATE_SUBSCRIPTION_GROUP: 3,
    ALREADY_SUBSCRIBED_TO_SUBSCRIPTION_GROUP: 4,
});
const parse$5 = (raw) => {
    if (!raw) {
        return ItemSaleRestrictions.NO_RESTRICTION;
    }
    const rawString = Type.number(raw, 'SaleRestriction');
    const type = Object.values(ItemSaleRestrictions).find(v => v === rawString);
    if (!type) {
        throw new ClientError(TRACK_ID('2019-05-14 02:11:07'), ClientErrorCode.UNEXPECTED_TYPE, `SaleRestriction type '${rawString}' unknown.`);
    }
    return type;
};
const WsItemSaleRestriction = { parse: parse$5 };

const WsItemAmountAndPriceTypes = Object.freeze({
    DEFAULT: 0,
    COMBINED_PRODUCT_REBATED: 1,
});
const parseType = (raw) => {
    if (!raw) {
        return WsItemAmountAndPriceTypes.DEFAULT;
    }
    const type = Object.values(WsItemAmountAndPriceTypes).find(v => v === raw);
    if (!type) {
        throw new ClientError(TRACK_ID('2019-06-21 02:50:07'), ClientErrorCode.UNEXPECTED_TYPE, `AmountAndPrice type '${raw}' unknown.`);
    }
    return type;
};
const parseSerialized$4 = (json) => ({
    type: parseType(Type.number(json.type, 'ShoppingCartItemAmountAndPrice.type')),
    amount: Type.number(json.amount, 'ShoppingCartItemAmountAndPrice.amount'),
    price: Money.parse(Type.number(json.price, 'ShoppingCartItemAmountAndPrice.price')),
});
const parse$4 = (json) => ({
    ...parseSerialized$4(json),
    priceHint: Type.optional.string(json.priceHint, 'ShoppingCartItemAmountAndPrice.priceHint'),
});
const serialize$4 = ({ type, amount, price }) => ({
    type: type,
    amount: amount,
    price: price,
});
const equals$3 = (lhs, rhs) => lhs.type === rhs.type && lhs.amount === rhs.amount && lhs.price === rhs.price;
const totalAmount = (amountAndPrices) => amountAndPrices.reduce((current, value) => current + value.amount, 0);
const price = (amountAndPrices) => amountAndPrices.reduce((current, value) => current + value.price * value.amount, 0);
const compare = (first, second) => first.type - second.type;
const sort = (amountAndPrices) => amountAndPrices.sort(compare);
const defaultOrFirstPrice = (amountAndPrices) => (amountAndPrices.length !== 0 ? sort([...amountAndPrices])[0].price : 0);
const WsItemAmountAndPrice = {
    parse: parse$4,
    parseSerialized: parseSerialized$4,
    serialize: serialize$4,
    equals: equals$3,
    totalAmount,
    price,
    sort,
    defaultOrFirstPrice,
};

const _commonParse$1 = (json) => ({
    articleId: Type.number(json.articleId, 'ShoppingCartItem.article ID'),
    itemRef: Type.string(json.itemRef, 'ShoppingCartItem.itemRef'),
    bundleId: Type.falsyToUndefined.number(json.bundleId, 'SerializedShoppingCartItem.bundleId'),
});
const parseSerialized$3 = (json) => {
    const type = serializeItemType(json.type);
    const selectedBundleItemsRaw = Type.optional.array(json.selectedBundleItems, 'SerializedShoppingCartItem.selectedBundleItems');
    return {
        ..._commonParse$1(json),
        amountAndPrices: Type.array(json.amountAndPrices, 'ShoppingCartItem.amountAndPrices').map(WsItemAmountAndPrice.parseSerialized),
        type,
        couponData: parseCouponDataIfRequired(json.couponData, type),
        selectedBundleItems: selectedBundleItemsRaw
            ? selectedBundleItemsRaw.map(WsBundleSelection.parseSerialized)
            : undefined,
        giftInfo: type === CartItemType.DOWNLOAD_GIFT ? WsGiftInfo.parseSerialized(json.giftInfo) : undefined,
    };
};
const parse$3 = (json) => {
    const oldPriceAmount = Type.optional.number(json.oldPrice, 'ShoppingCartItem.oldPrice');
    const type = parseItemType(json.type);
    return {
        ..._commonParse$1(json),
        type,
        amountAndPrices: WsItemAmountAndPrice.sort(Type.array(json.amountAndPrices, 'ShoppingCartItem.amountAndPrices').map(WsItemAmountAndPrice.parse)),
        title: Type.optional.string(json.title, 'ShoppingCartItem.title'),
        coverURL: Type.optional.string(json.coverUrlLarge, 'ShoppingCartItem.coverUrlLarge'),
        authors: Type.optional.string(json.authors, 'ShoppingCartItem.authors'),
        binderName: Type.optional.string(json.binderName, 'ShoppingCartItem.binderName'),
        productType: Type.falsyToUndefined.string(json.productType, 'ShoppingCartItem.productType'),
        availabilityData: json.availabilityData && WsAvailabilityData.parse(json.availabilityData),
        oldPrice: oldPriceAmount !== undefined ? Money.parse(oldPriceAmount) : undefined,
        productTypeDisplayName: Type.optional.string(json.productTypeDisplayName, 'ShoppingCartItem.productTypeDisplayName'),
        productTypeDescription: Type.optional.string(json.productTypeDescription, 'ShoppingCartItem.productTypeDescription'),
        couponData: parseCouponDataIfRequired(json.couponData, type),
        features: WsItemFeatures.parseShoppingCart(json.features),
        displayPublicationDate: Type.falsyToUndefined.string(json.displayPublicationDate, 'ShoppingCartItem.displayPublicationDate'),
        bundleId: Type.falsyToUndefined.number(json.bundleId, 'ShoppingCartItem.bundleId'),
        selectedBundleItems: Type.array(json.selectedBundleItems, 'ShoppingCartItem.selectedBundleItems').map(WsBundleSelection.parse),
        saleRestriction: WsItemSaleRestriction.parse(json.saleRestriction),
        isSubscription: Type.optional.boolean(json.subscription, 'ShoppingCartItem.subscription') || false,
        subscriptionDescription: Type.optional.string(json.subscriptionDescription, 'ShoppingCartItem.subscriptionDescription'),
        giftInfo: type === CartItemType.DOWNLOAD_GIFT ? WsGiftInfo.parse(json.giftInfo) : undefined,
        publicationDate: Type.optional.dateString(json.publicationDate, 'ShoppingCartItem.publicationDate'),
        dateOfDeliveryMin: Type.optional.dateString(json.dateOfDeliveryMin, 'ShoppingCartItem.dateOfDeliveryMin'),
        dateOfDeliveryMax: Type.optional.dateString(json.dateOfDeliveryMax, 'ShoppingCartItem.dateOfDeliveryMax'),
    };
};
const serialize$3 = ({ articleId, amountAndPrices, itemRef, type, giftInfo, couponData, bundleId, selectedBundleItems, }) => {
    const base = {
        articleId,
        amountAndPrices: amountAndPrices.map(WsItemAmountAndPrice.serialize),
        itemRef,
        type: serializeItemType(type),
    };
    if (type === CartItemType.COUPON) {
        return {
            ...base,
            couponData,
        };
    }
    else if (type === CartItemType.SERIAL_BUNDLE) {
        return {
            ...base,
            bundleId,
            selectedBundleItems: selectedBundleItems.map(WsBundleSelection.serialize),
        };
    }
    else if (type === CartItemType.DOWNLOAD_GIFT) {
        return {
            ...base,
            giftInfo: giftInfo && WsGiftInfo.serialize(giftInfo),
        };
    }
    return base;
};
const forTracking = (items) => items.reduce((accumulated, item) => accumulated.concat(item.amountAndPrices.map(amountAndPrice => ({
    articleId: item.articleId,
    amount: amountAndPrice.amount,
    price: amountAndPrice.price,
    title: item.title,
    binderName: item.binderName,
    productType: item.productType,
}))), []);
const equals$2 = (lhs, rhs) => lhs.articleId === rhs.articleId &&
    lhs.amountAndPrices.length === rhs.amountAndPrices.length &&
    lhs.amountAndPrices.every((value, index) => WsItemAmountAndPrice.equals(value, rhs.amountAndPrices[index])) &&
    lhs.itemRef === rhs.itemRef &&
    serializeItemType(lhs.type) === rhs.type;
const WsShoppingCartItem = { parse: parse$3, serialize: serialize$3, equals: equals$2, parseSerialized: parseSerialized$3, forTracking };

const ALL_DELIVERY_GROUP_TYPES = ['SHIPMENT', 'PICKUP', 'DOWNLOAD', 'DOWN_PREORD', 'UNKNOWN', 'DOWN_UNKNOWN'];
const parseDeliveryGroupType = (value) => {
    const str = Type.optional.string(value, 'DeliveryGroupType');
    const idx = ALL_DELIVERY_GROUP_TYPES.findIndex(type => type === str);
    if (idx === -1) {
        throw new ClientError(TRACK_ID('2022-07-06 16:19:10'), ClientErrorCode.UNEXPECTED_TYPE, `DeliveryGroupType '${value}' unknown.`);
    }
    return ALL_DELIVERY_GROUP_TYPES[idx];
};
const parse$2 = (json) => ({
    earliestDate: Type.optional.dateString(json.earliestDate, 'ShoppingCartDeliveryGroup.earliestDate'),
    itemRefs: Type.array(json.itemRefs, 'ShoppingCartDeliveryGroup.itemsRefs').map(String),
    latestDate: Type.optional.dateString(json.latestDate, 'ShoppingCartDeliveryGroup.latestDate'),
    type: parseDeliveryGroupType(json.type),
});
const WsShoppingCartDeliveryGroup = { parse: parse$2 };

const _commonParse = (json) => ({
    total: Money.parse(Type.number(json.total, 'ShoppingCart.total')),
    totalGoods: Money.parse(Type.number(json.totalGoods, 'ShoppingCart.totalGoods')),
    giftWrap: json.giftWrap ? WsGiftWrap.parse(Type.object(json.giftWrap, 'ShoppingCart.giftWrap')) : undefined,
});
const _items = (json) => ({
    valid: Type.optional.array(json.shoppingCartItems, 'ShoppingCart.shoppingCartItems') || [],
    invalid: Type.optional.array(json.invalidShoppingCartItems, 'ShoppingCart.invalidShoppingCartItems') || [],
    nonCollectable: Type.optional.array(json.notBuyableShoppingCartItems, 'ShoppingCart.notBuyableShoppingCartItems') || [],
});
const parse$1 = (json) => {
    const { valid, invalid, nonCollectable } = _items(json);
    const deliveryGroups = Type.optional.array(json.deliveryGroups, 'ShoppingCart.deliveryGroups') || [];
    return {
        deliveryGroups: deliveryGroups.map(WsShoppingCartDeliveryGroup.parse),
        items: valid.map(WsShoppingCartItem.parse),
        invalidItems: invalid.map(WsInvalidShoppingCartItem.parse),
        nonCollectableItems: nonCollectable.map(WsShoppingCartItem.parse),
        hasSaleRestriction: Type.boolean(json.hasSaleRestriction, 'ShoppingCart.hasSaleRestriction'),
        ..._commonParse(json),
    };
};
const parseSerialized$2 = (json) => {
    const { valid, invalid, nonCollectable } = _items(json);
    return {
        shoppingCartItems: valid.map(WsShoppingCartItem.parseSerialized),
        invalidShoppingCartItems: invalid.map(WsInvalidShoppingCartItem.parseSerialized),
        notBuyableShoppingCartItems: nonCollectable.map(WsShoppingCartItem.parseSerialized),
        ..._commonParse(json),
    };
};
const serialize$2 = ({ total, totalGoods, items, invalidItems, nonCollectableItems, giftWrap, }) => ({
    total,
    totalGoods,
    shoppingCartItems: items.map(WsShoppingCartItem.serialize),
    invalidShoppingCartItems: invalidItems.map(WsInvalidShoppingCartItem.serialize),
    notBuyableShoppingCartItems: nonCollectableItems.map(WsShoppingCartItem.serialize),
    giftWrap, // GiftWrap === SerializedGiftWrap
});
const isEmpty = (cart) => 
// only check valid items since invalid items are not displayed yet
cart.items.length === 0;
const isEmptySerialized = (cart) => 
// only check valid items since invalid items are not displayed yet
cart.shoppingCartItems.length === 0;
const equalsSerialized$1 = (lhs, rhs) => {
    if (lhs.total !== rhs.total ||
        lhs.totalGoods !== rhs.totalGoods ||
        lhs.items.length !== rhs.shoppingCartItems.length ||
        lhs.invalidItems.length !== rhs.invalidShoppingCartItems.length ||
        !WsGiftWrap.equals(lhs.giftWrap, rhs.giftWrap)) {
        return false;
    }
    return (lhs.items.every((item, i) => WsShoppingCartItem.equals(item, rhs.shoppingCartItems[i])) &&
        lhs.invalidItems.every((item, i) => WsInvalidShoppingCartItem.equals(item, rhs.invalidShoppingCartItems[i])));
};
const equals$1 = (lhs, rhs) => equalsSerialized$1(lhs, serialize$2(rhs));
const empty$1 = Object.freeze({
    deliveryGroups: [],
    items: [],
    invalidItems: [],
    nonCollectableItems: [],
    hasSaleRestriction: false,
    totalGoods: Money.zero,
    total: Money.zero,
});
const emptySerialized = serialize$2(empty$1);
const WsShoppingCart = {
    parse: parse$1,
    serialize: serialize$2,
    parseSerialized: parseSerialized$2,
    isEmpty,
    equals: equals$1,
    empty: empty$1,
    emptySerialized,
    equalsSerialized: equalsSerialized$1,
    isEmptySerialized,
};

const STANDARD = 'shoppingCart';
const QUICK_BUY = 'quickBuy';
const _ShoppingCartStore = { useSession: false };
/**
 * Favor the session storage token over the local storage token.
 */
const _get = (allowSession) => {
    if (allowSession && _ShoppingCartStore.useSession) {
        return Persistore.session.get(QUICK_BUY);
    }
    return Persistore.get(STANDARD);
};
const _set = (value, allowSession) => {
    if (allowSession && _ShoppingCartStore.useSession) {
        Persistore.session.set(QUICK_BUY, value);
    }
    else {
        Persistore.set(STANDARD, value);
    }
    window.dispatchEvent(new Event('storage'));
};
const _getAndParse = (allowSession) => () => {
    try {
        const shoppingCartJson = _get(allowSession);
        if (shoppingCartJson)
            return WsShoppingCart.parseSerialized(JSON.parse(shoppingCartJson));
    }
    catch (e) {
        Track.error(TRACK_ID('2018-06-07 03:44:55'), 'error parsing shopping cart from local storage: ' + e.message);
    }
    return WsShoppingCart.emptySerialized;
};
const _serializeAndSet = (allowSession) => (cart) => {
    try {
        _set(JSON.stringify(WsShoppingCart.serialize(cart)), allowSession);
    }
    catch (e) {
        throw new ClientError(TRACK_ID('2018-08-24 05:09:32'), ClientErrorCode.STORAGE_WRITE_ERROR, 'Unable to write shopping cart to storage: ' + e.message);
    }
};
const get$1 = _getAndParse(true);
const getLocal = _getAndParse(false);
const set$1 = _serializeAndSet(true);
const setLocal = _serializeAndSet(false);
const setSession = (cart) => {
    _ShoppingCartStore.useSession = true;
    ShoppingCartStore.set(cart);
};
const setSerialized$1 = (cart) => {
    try {
        _set(JSON.stringify(cart), true);
    }
    catch (e) {
        throw new ClientError(TRACK_ID('2019-10-10 02:39:09'), ClientErrorCode.STORAGE_WRITE_ERROR, 'Unable to write shopping cart to storage: ' + e.message);
    }
};
const _removeSession = () => Persistore.session.remove(QUICK_BUY);
const init$2 = (allowSession) => {
    if (allowSession) {
        _ShoppingCartStore.useSession = !!Persistore.session.get(QUICK_BUY);
    }
    else {
        _ShoppingCartStore.useSession = false;
        // this behaviour will later on make other applications
        // drop the session cart if present, when visiting
        // e.g. the bookshop will drop the session cart and the customer
        //      would need to click onto the quick buy button again
        _removeSession();
    }
};
/**
 * Removes the currently used cart for quick buy or standard cart.
 */
const remove$1 = () => {
    if (_ShoppingCartStore.useSession) {
        _ShoppingCartStore.useSession = false;
        _removeSession();
    }
    else {
        Persistore.remove(STANDARD);
    }
};
const isSessionMode = () => _ShoppingCartStore.useSession;
const ShoppingCartStore = {
    get: get$1,
    set: set$1,
    remove: remove$1,
    init: init$2,
    setSession,
    isSessionMode,
    getLocal,
    setLocal,
    setSerialized: setSerialized$1,
};

const CART_ID = 'header-cart';
const _Header = {
    state: { container: null, isHudu: true },
    render: (container) => {
        const cart = ShoppingCartStore.getLocal();
        const count = _getCartItemCount(cart);
        const totalGoods = _getCartTotalGoods(cart);
        const content = _cartTotal(totalGoods) + _cartCount(count) + _cartText(count);
        container.innerHTML = count === 0 ? _cartEmpty(content) : _cartLink(content);
    },
};
const init$1 = (isHudu) => {
    _Header.state.isHudu = isHudu;
    const container = Globals.document().getElementById(CART_ID);
    _Header.state.container = container;
    if (container) {
        _Header.render(container);
        Globals.window().addEventListener('focus', Header.update);
    }
};
const update$1 = () => {
    const container = _Header.state.container;
    if (container) {
        _Header.render(container);
    }
    else {
        init$1(process.env.IS_HUDU);
    }
};
const _cartLink = (content) => `<a href="${Header.href}" onclick="return window.libri.Cart.Header.onClick();"><div class="cart-content">${content}</div></a>`;
const _cartEmpty = (content) => `<div class="cart-content cart-empty">${content}</div>`;
const _getCartTotalGoods = (cart) => [...cart.shoppingCartItems, ...cart.notBuyableShoppingCartItems].reduce((result, item) => result + WsItemAmountAndPrice.price(item.amountAndPrices), 0);
const _cartTotal = (totalGoods) => `<div class="cart-total-goods"><span class="label">Warenkorb</span><span class="price">${Money.getFormatted(totalGoods)}</span></div>`;
const _cartCount = (count) => `<div class="cart-count">${count}</div>`;
const _cartText = (count) => `<div class="cart-text">${_Header.state.isHudu && count === 0 ? 'Buch dabei, portofrei' : 'Zur Kasse'}</div>`;
const _getCartItemCount = (cart) => [...cart.shoppingCartItems, ...cart.notBuyableShoppingCartItems].reduce((result, item) => result + WsItemAmountAndPrice.totalAmount(item.amountAndPrices), 0);
// this is the default onClick handler
// but we want to allow to override it's behaviour
// especially the checkout cart page will make the button link
// to the checkout after refreshing the JWTs by the bookshop
const onClick = () => { };
// this is the default href of the header cart
// but it will be overridden e.g. on the cart page
const href = '/de/shoppingcart';
const Header = { init: init$1, update: update$1, onClick, href };

var CouponType;
(function (CouponType) {
    CouponType["COUPON"] = "LIBRIDE";
    CouponType["GIFT_CARD"] = "EASY_CASH";
})(CouponType || (CouponType = {}));
const _parseCouponType = (s) => {
    const result = Object.values(CouponType).find(v => v === s);
    if (!result) {
        throw new ClientError(TRACK_ID('2019-02-19 09:23:37'), ClientErrorCode.UNEXPECTED_TYPE, 'Unknown coupon type: ' + s);
    }
    return result;
};
const parseValid = (json) => {
    const type = _parseCouponType(Type.string(json.type, 'ValidCoupon.type'));
    const code = Type.string(json.code, 'ValidCoupon.code');
    const pin = Type.falsyToUndefined.string(json.pin, 'ValidCoupon.pin');
    return Object.freeze({ type, code, pin });
};
const parseInvalid = (json) => {
    const typeString = Type.falsyToUndefined.string(json.type, 'InvalidCoupon.type');
    const type = typeString ? _parseCouponType(typeString) : undefined;
    const code = Type.falsyToUndefined.string(json.code, 'InvalidCoupon.code');
    const pin = Type.falsyToUndefined.string(json.pin, 'InvalidCoupon.pin');
    const reasonCode = Type.number(json.reasonCode, 'InvalidCoupon.reasonCode');
    return Object.freeze({ type, code, pin, reasonCode });
};
const serialize$1 = ({ type, code, pin, }) => ({
    type,
    code,
    pin,
});
const parseSerialized$1 = (json) => {
    const typeString = Type.falsyToUndefined.string(json.type, 'Coupon.type');
    const type = typeString ? _parseCouponType(typeString) : undefined;
    const code = Type.falsyToUndefined.string(json.code, 'Coupon.code');
    const pin = Type.falsyToUndefined.string(json.pin, 'Coupon.pin');
    return Object.freeze({ type, code, pin });
};
const equals = (left, right) => left.type === right.type && left.code === right.code && left.pin === right.pin;
const WsCoupon = {
    parseValid,
    serialize: serialize$1,
    parseInvalid,
    parseSerialized: parseSerialized$1,
    equals,
};

const empty = Object.freeze({ validCoupons: Object.freeze([]), invalidCoupons: Object.freeze([]) });
const parse = (json) => ({
    validCoupons: (Type.optional.array(json.validCoupons, 'Coupons.validCoupons') || []).map(WsCoupon.parseValid),
    invalidCoupons: (Type.optional.array(json.invalidCoupons, 'Coupons.invalidCoupons') || []).map(WsCoupon.parseInvalid),
});
const serialize = (coupons) => {
    const validCoupons = coupons.validCoupons.map(WsCoupon.serialize);
    const invalidCoupons = coupons.invalidCoupons.map(WsCoupon.serialize);
    return { validCoupons, invalidCoupons };
};
const serializeValid = (validCoupons) => ({
    validCoupons: validCoupons.map(WsCoupon.serialize),
    invalidCoupons: Object.freeze([]),
});
const parseSerialized = (json) => ({
    validCoupons: (Type.optional.array(json.validCoupons, 'Coupons.validCoupons') || []).map(WsCoupon.parseSerialized),
    invalidCoupons: (Type.optional.array(json.invalidCoupons, 'Coupons.invalidCoupons') || []).map(WsCoupon.parseSerialized),
});
const equalsSerialized = (couponsParam, serializedCouponsParam) => {
    if (!couponsParam) {
        return !serializedCouponsParam;
    }
    else if (!serializedCouponsParam) {
        return false;
    }
    const coupons = couponsParam;
    const serializedCoupons = serializedCouponsParam;
    if (coupons.validCoupons.length !== serializedCoupons.validCoupons.length ||
        coupons.invalidCoupons.length !== serializedCoupons.invalidCoupons.length) {
        return false;
    }
    return (coupons.validCoupons.every((left, i) => WsCoupon.equals(left, serializedCoupons.validCoupons[i])) &&
        coupons.invalidCoupons.every((left, i) => WsCoupon.equals(left, serializedCoupons.invalidCoupons[i])));
};
const hasOnlyGiftCards = (coupons) => coupons.validCoupons.every(coupon => coupon.type === CouponType.GIFT_CARD);
const WsCoupons = {
    parse,
    serialize,
    serializeValid,
    parseSerialized,
    hasOnlyGiftCards,
    equalsSerialized,
    empty,
};

const couponsName = 'coupons';
const get = () => {
    try {
        // Tolino devices
        // - remove all cookies when redirecting to the login page
        // - have no local storage (so cookies are used to store the coupons as fallback)
        // - do not wipe the session storage when redirecting to the login page
        // => tolino devices should store the coupons in the session storage
        const couponsJson = Persistore.get(couponsName);
        if (couponsJson) {
            return WsCoupons.parseSerialized(JSON.parse(couponsJson));
        }
    }
    catch (e) {
        Track.error(TRACK_ID('2019-02-19 01:55:24'), 'error parsing coupons from local storage: ' + e.message);
    }
    return WsCoupons.empty;
};
const _setSerialized = (coupons) => {
    // Tolino devices
    // - remove all cookies when redirecting to the login page
    // - have no local storage (so cookies are used to store the coupons as fallback)
    // - do not wipe the session storage when redirecting to the login page
    // => tolino devices should store the coupons in the session storage
    Persistore.set(couponsName, JSON.stringify(coupons));
};
const append = (coupon) => {
    const { validCoupons: currentValid, invalidCoupons: currentInvalid } = get();
    const coupons = { invalidCoupons: currentInvalid, validCoupons: [...currentValid, WsCoupon.serialize(coupon)] };
    _setSerialized(coupons);
};
const set = (coupons) => {
    _setSerialized(WsCoupons.serialize(coupons));
};
const remove = () => {
    Persistore.remove(couponsName);
};
const CouponsStore = { get, set, append, remove };

const WS = {
    shoppingCart: {
        add: createCall({ name: 'shoppingcart/add', parser: WsShoppingCart.parse, method: Method.POST }),
        // add multiple articles
        addArticles: createCall({ name: 'shoppingcart/addarticles', parser: WsShoppingCart.parse, method: Method.POST }),
        // add an article by a number (ean or isbn)
        addByNumber: createCall({ name: 'shoppingcart/addbynumber', parser: WsShoppingCart.parse, method: Method.POST }),
        addCouponItem: createCall({ name: 'shoppingcart/addcouponitem', parser: WsShoppingCart.parse, method: Method.POST }),
        addDownloadGift: createCall({
            name: 'shoppingcart/adddownloadgift',
            parser: WsShoppingCart.parse,
            method: Method.POST,
        }),
        addWishlist: createCall({
            name: 'shoppingcart/addwishlist',
            parser: WsShoppingCart.parse,
            method: Method.POST,
        }),
        changeAmount: createCall({ name: 'shoppingcart/changeamount', parser: WsShoppingCart.parse, method: Method.POST }),
        checkCoupon: createCall({
            name: 'shoppingcart/checkcoupon',
            parser: WsCoupon.parseValid,
            method: Method.GET,
        }),
        mergeAtLogin: createCall({
            name: 'shoppingcart/mergeatlogin',
            parser: WsShoppingCart.parse,
            method: Method.POST,
            tokenCheck: TokenChecks.isCustomer,
        }),
    },
};

const _Cart = {};
const articleTracking = (cart, articleId, amount, trackTarget, info) => {
    //mapping WsShoppingCartItems to TrackingItem and filter for the articleId
    const trackingItem = WsShoppingCartItem.forTracking(cart.items).filter(trackingItem => trackingItem.articleId === articleId)[0];
    // we finally wait until the tracking finished
    return Track.article(TRACK_ID('2019-05-02 02:54:02'), trackingItem ? trackingItem : { articleId }, amount, 'ADD', info ? info.source : 'UNKNOWN', trackTarget, trackingItem
        ? undefined
        : new ClientError(TRACK_ID('2019-12-20 10:34:42'), ClientErrorCode.ARTICLE_ID_MISMATCHING, 'Article was not added to Shoppingcart'), info ? info.index : undefined, info ? info.rating : undefined);
};
const UPDATE_CART = 'updatecart';
/**
 * Update number of articles present in the Cart
 */
_Cart.update = () => {
    // Bookshop Cart update (Header and its functions affect the Bookshop only)
    Header.update();
    // Mobileshop update (An event handler in the Mobileshop-Header listens to this custom event)
    Globals.window().dispatchEvent(new CustomEvent(UPDATE_CART));
};
_Cart.addInternal = (articleId, amount, persistFlag, quickBuy, info) => {
    const trackTarget = quickBuy ? 'QUICK_BUY' : 'BASKET';
    const cart = quickBuy ? WsShoppingCart.emptySerialized : ShoppingCartStore.get();
    const localPersist = quickBuy ? ShoppingCartStore.setSession : ShoppingCartStore.set;
    return WS.shoppingCart
        .add({
        shoppingCart: WsParser.toApiJson(cart),
        articleId,
        amount,
        itemRef: _.randomHexString(32),
        persistFlag,
    })
        .then((cart) => {
        // we directly persist the shopping cart into the local storage,
        // since this method will be called outside of the checkout
        // and the user only wants to fire and forget here
        localPersist(cart);
        _Cart.update();
        return articleTracking(cart, articleId, amount, trackTarget, info).then(() => cart);
    })
        .catch(error => {
        // we do not wait until the tracking finished in error case
        Track.article(TRACK_ID('2019-05-02 02:54:06'), { articleId }, amount, 'ADD', 'BASKET', trackTarget, error);
        return error;
    });
};
_Cart.add = ({ articleId, amount, persistFlag, info }) => _Cart.addInternal(articleId, amount, persistFlag, false, info);
_Cart.addEnqueued = _.enqueueAsync(_Cart.add);
const add = (articleId, amount = 1, persistFlag = true, info) => _Cart.addEnqueued({ articleId, amount, persistFlag, info });
// Temporary solution approved by PM to add multiple articles to the cart without tracking.
// This will be adapted when the Track module will support tracking multiple articles e.g. Track.articles()
_Cart.addArticlesInternal = (articleIds, persistFlag, info) => {
    const cart = ShoppingCartStore.get();
    const localPersist = ShoppingCartStore.set;
    return WS.shoppingCart
        .addArticles({
        shoppingCart: WsParser.toApiJson(cart),
        articleIds: articleIds.join(','),
        persistFlag,
    })
        .then((cart) => {
        // we directly persist the shopping cart into the local storage,
        // since this method will be called outside of the checkout
        // and the user only wants to fire and forget here
        localPersist(cart);
        _Cart.update();
        return cart;
    })
        .catch(() => { });
};
_Cart.addArticles = ({ articleIds, persistFlag, info }) => _Cart.addArticlesInternal(articleIds, persistFlag, info);
_Cart.addArticlesEnqueued = _.enqueueAsync(_Cart.addArticles);
const addArticles = (articleIds, persistFlag = true, info) => _Cart.addArticlesEnqueued({ articleIds, persistFlag, info });
_Cart.addByNumberInternal = (articleNumber, amount, persistFlag) => WS.shoppingCart
    .addByNumber({
    shoppingCart: WsParser.toApiJson(ShoppingCartStore.get()),
    articleNumber,
    amount,
    itemRef: _.randomHexString(32),
    persistFlag,
})
    .then((cart) => {
    // we directly persist the shopping cart into the local storage,
    // since this method will be called outside of the checkout
    // and the user only wants to fire and forget here
    ShoppingCartStore.set(cart);
    _Cart.update();
    Track.article(TRACK_ID('2020-01-10 16:56:15'), { articleId: Number(articleNumber) }, amount, 'ADD', 'URL', 'BASKET');
    return cart;
})
    .catch(error => {
    // we do not wait until the tracking finished in error case
    Track.article(TRACK_ID('2020-01-10 14:40:03'), { articleId: Number('articleNumber'), amount: amount }, amount, 'ADD', 'URL', 'BASKET', error);
});
_Cart.addByNumber = ({ articleNumber, amount, persistFlag }) => _Cart.addByNumberInternal(articleNumber, amount, persistFlag);
_Cart.addByNumberEnqueued = _.enqueueAsync(_Cart.addByNumber);
const addByNumber = (articleNumber, amount = 1, persistFlag = true) => _Cart.addByNumberEnqueued({ articleNumber, amount, persistFlag });
// the quick buy does not persist the cart in our DB
const quickBuy = (articleId, info) => _Cart.addInternal(articleId, 1, false, true, info);
const addCouponItem = (couponData, persistFlag = true) => WS.shoppingCart
    .addCouponItem({
    shoppingCart: WsParser.toApiJson(ShoppingCartStore.get()),
    itemRef: _.randomHexString(32),
    persistFlag,
    ...couponData,
})
    .then((cart) => {
    // we directly persist the shopping cart into the local storage,
    // since this method will be called outside of the checkout
    // and the user only wants to fire and forget here
    ShoppingCartStore.set(cart);
    _Cart.update();
    return cart;
})
    .catch(error => {
    Track.error(TRACK_ID('2019-05-02 11:50:13'), 'Coupon item could not be added to the cart, because: ' + error.message);
});
const addDownloadGift = (articleId, deliveryDate, from, to, message, receiverEmail, persistFlag = true) => WS.shoppingCart
    .addDownloadGift({
    shoppingCart: WsParser.toApiJson(ShoppingCartStore.get()),
    itemRef: _.randomHexString(32),
    articleId,
    deliveryDate: deliveryDate.toISOString(),
    from,
    to,
    message,
    receiverEmail,
    persistFlag,
})
    .then((cart) => {
    // we directly persist the shopping cart into the local storage,
    // since this method will be called outside of the checkout
    // and the user only wants to fire and forget here
    ShoppingCartStore.set(cart);
    _Cart.update();
    return cart;
})
    .catch(error => {
    Track.error(TRACK_ID('2019-08-29 11:49:28'), 'Download gift could not be added to the cart, because: ' + error.message);
});
const addWishlist = (wishlistId, persistFlag = true) => WS.shoppingCart
    .addWishlist({
    shoppingCart: WsParser.toApiJson(WsShoppingCart.emptySerialized),
    wishlistId,
    persistFlag,
})
    .then((cart) => {
    // we directly persist the shopping cart into the local storage,
    // since this method will be called outside of the checkout
    // and the user only wants to fire and forget here
    ShoppingCartStore.set(cart);
    _Cart.update();
    return cart;
})
    .catch(error => {
    // we do not wait until the tracking finished in error case
    Track.error(TRACK_ID('2020-10-14 03:41:19'), 'Wishlist ' + wishlistId + ' could not be added to the cart, because: ' + error.message);
});
const changeAmount = (current, item, amountDiff, persistFlag = true) => {
    const { articleId, amountAndPrices, title, authors, binderName, productType, availabilityData, itemRef } = item;
    const articleAction = WsItemAmountAndPrice.totalAmount(amountAndPrices) + amountDiff === 0 ? 'REMOVE' : 'EDIT';
    const price = WsItemAmountAndPrice.defaultOrFirstPrice(amountAndPrices);
    const trackingArticle = {
        articleId,
        price,
        title,
        authors,
        binderName,
        productType,
        availabilityMessage: availabilityData && availabilityData.text,
    };
    return WS.shoppingCart
        .changeAmount({
        shoppingCart: WsParser.toApiJson(WsShoppingCart.serialize(current)),
        amount: amountDiff,
        itemRef,
        persistFlag,
    })
        .then((cart) => {
        _Cart.update();
        // we finally wait until the tracking finished
        return Track.article(TRACK_ID('2018-10-26 01:49:41'), trackingArticle, amountDiff, articleAction, 'BASKET', 'BASKET').then(() => cart);
    })
        .catch(error => {
        // we do not wait until the tracking finished in error case
        Track.article(TRACK_ID('2018-11-20 09:32:44'), trackingArticle, amountDiff, articleAction, 'BASKET', 'BASKET', error);
    });
};
// ERROR-Handling will be done by the clients
const checkCoupon = (couponCode) => WS.shoppingCart.checkCoupon({ couponType: CouponType.COUPON, couponCode });
// ERROR-Handling will be done by the clients
const checkGiftCard = (couponCode, couponPin) => WS.shoppingCart.checkCoupon({ couponType: CouponType.GIFT_CARD, couponCode, couponPin });
_Cart.mergeAtLogin = (current) => WS.shoppingCart.mergeAtLogin({ shoppingCart: WsParser.toApiJson(current) }).catch(error => {
    Track.error(TRACK_ID('2019-05-02 11:50:06'), 'Cart merge at login failed, because: ' + error.message);
});
/**
 * ATTENTION: It is very important that the merge considers only the
 * standard cart and persists the result for the standard cart.
 * Therefore we explicitly "getLocal" and "setLocal".
 */
const mergeAtLoginAndUpdate = () => _Cart.mergeAtLogin(ShoppingCartStore.getLocal()).then(cart => {
    if (cart) {
        ShoppingCartStore.setLocal(cart);
        _Cart.update();
    }
    return cart;
});
const tryMergeAtLogin = (given) => Cart.mergeAtLoginAndUpdate().then(cart => 
// return the given cart as best available fallback
cart ? WsShoppingCart.serialize(cart) : given);
const update = (nextCart, nextCoupons) => {
    ShoppingCartStore.set(nextCart);
    nextCoupons && CouponsStore.set(nextCoupons);
};
const clear = () => {
    ShoppingCartStore.remove();
    CouponsStore.remove();
};
const current = () => ShoppingCartStore.get();
const currentCoupons = () => CouponsStore.get();
const appendCoupon = CouponsStore.append;
const isQuickBuy = ShoppingCartStore.isSessionMode;
const init = ({ supportsQuickBuy }) => ShoppingCartStore.init(supportsQuickBuy);
const setSerialized = ShoppingCartStore.setSerialized;
const getItemsCount = () => {
    const cart = ShoppingCartStore.getLocal();
    return [...cart.shoppingCartItems, ...cart.notBuyableShoppingCartItems].reduce((result, item) => result + WsItemAmountAndPrice.totalAmount(item.amountAndPrices), 0);
};
const Cart = {
    add,
    addArticles,
    addByNumber,
    quickBuy,
    addCouponItem,
    addDownloadGift,
    addWishlist,
    changeAmount,
    checkCoupon,
    checkGiftCard,
    mergeAtLoginAndUpdate,
    tryMergeAtLogin,
    update,
    clear,
    current,
    currentCoupons,
    appendCoupon,
    Header,
    isQuickBuy,
    init,
    setSerialized,
    getItemsCount,
};

// TODO(MW): Please delete this, after implementation of new Prudsys call
const FALLBACK_RECOMMENDATIONS = {
    fallbackProducts: [],
    lastProducts: [],
    personals: [],
    products: [],
};
const _add = (id, info) => Cart.add(id, 1, true, info);
const _addAll = (ids, info) => Cart.addArticles(ids, true, info);
const addToCart = async (articleId, info) => {
    const result = (await _add(articleId, info));
    if (result === null || result === void 0 ? void 0 : result.errorCode) {
        return Promise.reject(result);
    }
    return Prudsys.recommendations('BASKETLAYER', 'DESKTOP', articleId);
};
// This is only a temporary solution, that is approved by PM. This service must be adapted, when backend is ready!
// TODO(MW): New Prudsyscall must be implemented to send a list of pids -> BASKETLAYERMULTI
const addWishlistToCart = async (wishlistId, articleIds) => {
    await Cart.addWishlist(wishlistId);
    await Prudsys.Dispatcher.basket(articleIds.map(id => ({ articleId: id, amount: 1 })));
    // return Prudsys.recommendations('HUG_BASKETLAYERMULTI', ShopVariant.getPrudsysApplicationType(), articleIds);
    return Promise.resolve(FALLBACK_RECOMMENDATIONS);
};
// This is only a temporary solution, that is approved by PM. This service must be adapted, when backend is ready!
// TODO(MW): New Prudsyscall must be implemented to send a list of pids -> BASKETLAYERMULTI
const addAllToCart = async (articleIds, info) => {
    await _addAll(articleIds, info);
    await Prudsys.Dispatcher.basket(articleIds.map(id => ({ articleId: id, amount: 1 })));
    // return Prudsys.recommendations('HUG_BASKETLAYERMULTI', ShopVariant.getPrudsysApplicationType(), articleIds);
    return Promise.resolve(FALLBACK_RECOMMENDATIONS);
};
const addDownloadGiftToCart = async (downloadGift) => {
    const { articleId, deliveryDate, from, message, receiverEmail, to } = downloadGift;
    await Cart.addDownloadGift(articleId, deliveryDate, from, to, message, receiverEmail);
    await Prudsys.Dispatcher.basket([{ articleId, amount: 1 }]);
    return Prudsys.recommendations('BASKETLAYER', 'DESKTOP', articleId);
};
const BasketService = { addToCart, addAllToCart, addWishlistToCart, addDownloadGiftToCart };

export { BasketService, Cart, CartItemType, CouponType, ItemSaleRestrictions, WsBundleSelection, WsCoupon, WsCoupons, WsGiftWrap, WsInvalidShoppingCartItem, WsItemAmountAndPrice, WsItemAmountAndPriceTypes, WsItemFeatures, WsItemSaleRestriction, WsShoppingCart, WsShoppingCartDeliveryGroup, WsShoppingCartItem, parseItemType };
